import { CreditCard, PackageCheck } from "lucide-react";
import Link from "next/link";
import { useEffect, useMemo, useState } from "react";

import { DashboardShell } from "@/components/dashboard/dashboard-shell";
import { Badge } from "@/components/ui/badge";
import { apiGet } from "@/services/api";
import { formatPrice } from "@/lib/utils";
import type { OrderWithItems } from "@/types/prisma";

/**
 * `app/dashboard/payments/page.tsx`.
 *
 * The server component filtered `paymentStatus: "PAID"` in a Prisma query with
 * `take: 20`. `/api/orders` returns the same rows for the signed-in user, so the
 * filter and the cap are applied here instead — identical output.
 */
export function PaymentsPage() {
  const [paidOrders, setPaidOrders] = useState<OrderWithItems[] | null>(null);

  useEffect(() => {
    let active = true;
    void apiGet<{ orders: OrderWithItems[] }>("/api/orders").then((data) => {
      if (!active) return;
      setPaidOrders(
        (data?.orders ?? [])
          .filter((order) => order.paymentStatus === "PAID")
          .slice(0, 20),
      );
    });
    return () => {
      active = false;
    };
  }, []);

  const orders = useMemo(() => paidOrders ?? [], [paidOrders]);

  return (
    <DashboardShell title="Payments">
      <div className="space-y-4">
        <div className="amazon-card">
          <div className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-amazon-orange" />
            <h2 className="font-bold">Payment method</h2>
          </div>
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
            Payments are processed securely through Stripe. No card details are stored on this server.
          </p>
          <div className="mt-4 rounded border border-dashed border-slate-300 p-4 text-sm dark:border-white/20">
            <p className="font-bold">Stripe Checkout</p>
            <p className="mt-0.5 text-xs text-slate-500">Active payment processor</p>
          </div>
        </div>

        <div className="amazon-card">
          <div className="flex items-center gap-2">
            <PackageCheck className="h-5 w-5 text-amazon-teal" />
            <h2 className="font-bold">Payment history</h2>
          </div>

          {paidOrders === null ? (
            <div className="mt-5 space-y-3">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-16 animate-pulse rounded bg-slate-100 dark:bg-white/5" />
              ))}
            </div>
          ) : orders.length === 0 ? (
            <div className="mt-5 text-center text-sm text-slate-500">
              <p>No completed payments yet.</p>
              <Link href="/search" className="mt-3 inline-flex text-amazon-teal hover:underline">
                Start shopping
              </Link>
            </div>
          ) : (
            <div className="mt-4 divide-y divide-slate-200 dark:divide-white/10">
              {orders.map((order) => (
                <div key={order.id} className="py-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-sm font-bold">{formatPrice(order.total)}</p>
                      <p className="mt-0.5 text-xs text-slate-500">
                        {new Date(order.createdAt).toLocaleDateString()} &bull;{" "}
                        {order.items.slice(0, 2).map((i) => i.title).join(", ")}
                        {order.items.length > 2 ? ` +${order.items.length - 2} more` : ""}
                      </p>
                      {order.stripePaymentIntent && (
                        <p className="mt-0.5 text-xs text-slate-400">
                          Payment ID: {order.stripePaymentIntent}
                        </p>
                      )}
                    </div>
                    <Badge tone="success">PAID</Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </DashboardShell>
  );
}